/**
 * THE PHONE GROUPED LIST (frame 56, phone display build PR1): the engine's tree rendered as
 * rails over compact rows — no column grid, no table semantics; each screen supplies the row
 * (`renderRow`). The desk keeps `DatasheetView`; this is the phone's twin over the SAME
 * `DisplayTree`, so grouping, order and collapse are one engine at both widths.
 *
 * Rows are numbered in VISUAL order (one `flattenRows` pass) so the shared selection
 * gestures (select-box.tsx: shift-click ranges, drag) work exactly as on the sheet — the
 * index rides `data-row-index` on the row the screen renders. Multi-membership rows appear
 * once per group; their React key includes the section path.
 */
import type { ReactNode, RefObject } from 'react';
import { WindowedRows } from './windowed-rows.js';
import { flattenRows, type DisplayTree, type GroupSection, type RailTone } from './engine.js';
import { GroupRail, railColourClass } from './group-rail.js';

export function GroupedList<Row>({
  tree,
  rowKey,
  renderRow,
  collapsed,
  onToggle,
  toneOf,
  listRef,
  label = 'Grouped list',
  windowed = false,
  rowHeight = 64,
}: {
  tree: DisplayTree<Row>;
  rowKey: (row: Row) => string;
  /** The row element itself — a `li.gl-row` (CompactRow) carrying `data-row-index={index}`. */
  renderRow: (row: Row, index: number) => ReactNode;
  collapsed: ReadonlySet<string>;
  onToggle: (path: string) => void;
  /** The rail tone for a section (frame 56's toned level-2 rails), from the axis's `tone`. */
  toneOf?: (section: GroupSection<Row>) => RailTone | undefined;
  listRef?: RefObject<HTMLDivElement>;
  label?: string;
  /** WINDOWED (0.1.474): only the bands of rows near the viewport are in the DOM — see
   *  `windowed-rows.tsx`. Off by default; the Items list turns it on (and off to print). */
  windowed?: boolean;
  /** The spacer estimate per row until a band is measured — the compact row's 4rem. */
  rowHeight?: number;
}): JSX.Element {
  const order = flattenRows(tree);
  const indexOf = new Map<string, number>();
  order.forEach((r, i) => {
    const k = rowKey(r);
    if (!indexOf.has(k)) indexOf.set(k, i);
  });
  const rows = (list: Row[]): ReactNode => (
    <WindowedRows
      rows={list}
      windowed={windowed}
      rowHeight={rowHeight}
      className="gl-band"
      render={(slice) => (
        <ul className="gl-rows">{slice.map((r) => <RowKeyed key={rowKey(r)} node={renderRow(r, indexOf.get(rowKey(r)) ?? 0)} />)}</ul>
      )}
    />
  );
  // A nested rail under a TONED parent wears the parent's tone (the datasheet's rule, 0.1.457).
  const section = (s: GroupSection<Row>, parentTone?: RailTone): ReactNode => {
    const isCollapsed = collapsed.has(s.path);
    const tone = toneOf?.(s) ?? parentTone;
    return (
      // The group's colour class rides the wrapper as on the desk sheet, so `--rail-hue` reaches
      // its rows — every grouped row's stroke is its group's colour (0.1.527; styles.css
      // GROUPED ROWS BEGIN AT THEIR STROKE).
      <div key={s.path} className={`gl-group gl-depth-${String(s.depth)}${railColourClass(s, tone) === '' ? '' : ` ${railColourClass(s, tone)}`}`}>
        <GroupRail section={s} collapsed={isCollapsed} onToggle={onToggle} tone={tone} />
        {!isCollapsed && (s.children.length > 0 ? s.children.map((c) => section(c, tone)) : rows(s.rows))}
      </div>
    );
  };
  return (
    // `gl-flat` marks rows under NO grouping — their stroke stays at the row's left edge
    // (0.1.527; maintainer, 3 Oct 2026; styles.css THE ROW'S ONE STROKE says why).
    <div className={tree.flatRows !== null ? 'gl gl-flat' : 'gl'} ref={listRef} aria-label={label}>
      {tree.flatRows !== null ? rows(tree.flatRows) : tree.sections.map((s) => section(s))}
    </div>
  );
}

/** A keyed pass-through so the screen's row can be any element (a plain fragment key). */
function RowKeyed({ node }: { node: ReactNode }): JSX.Element {
  return <>{node}</>;
}
